import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronDown, MoreHorizontal } from 'lucide-react';

interface DropdownItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'destructive';
}

interface DropdownProps {
  trigger?: React.ReactNode;
  items: DropdownItem[];
  align?: 'left' | 'right';
  className?: string;
}

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownTrigger = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.foreground};
  transition: ${props => props.theme.transitionSmooth};
  
  &:hover {
    background: ${props => props.theme.muted};
  }
`;

const DropdownContent = styled.div<{ $isOpen: boolean; $align: string }>`
  position: absolute;
  top: 100%;
  ${props => props.$align === 'right' ? 'right: 0;' : 'left: 0;'}
  margin-top: 8px;
  background: ${props => props.theme.card};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadowSoft};
  min-width: 200px;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 0.2s ease;
`;

const DropdownItem = styled.button<{ $variant: string }>`
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${props => props.theme.foreground};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.muted};
  }

  ${props => props.$variant === 'destructive' && `
    color: ${props.theme.destructive};
    
    &:hover {
      background-color: ${props.theme.destructive}10;
    }
  `}

  svg {
    height: 16px;
    width: 16px;
  }
`;

export const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  align = 'right',
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = (item: DropdownItem) => {
    item.onClick();
    setIsOpen(false);
  };

  return (
    <DropdownContainer ref={dropdownRef} className={className}>
      <DropdownTrigger onClick={() => setIsOpen(!isOpen)}>
        {trigger || <MoreHorizontal size={16} />}
      </DropdownTrigger>
      <DropdownContent $isOpen={isOpen} $align={align}>
        {items.map((item, index) => (
          <DropdownItem
            key={index}
            onClick={() => handleItemClick(item)}
            $variant={item.variant || 'default'}
          >
            {item.icon}
            {item.label}
          </DropdownItem>
        ))}
      </DropdownContent>
    </DropdownContainer>
  );
}; 